import { acceptResult, newNonce, RENDER_REQUEST } from "../../sandbox/protocol.ts";

/**
 * Draws a Mermaid diagram in the sandboxed frame (design §4): a fresh
 * `<iframe sandbox="allow-scripts">` per diagram (never `allow-same-origin`: an opaque origin with
 * no cookies, storage or access to the app), one nonce-checked message each way, then the frame
 * is removed. Resolves to the frame's SVG text, which the caller sanitises and shows as an image;
 * rejects on a failed render, a bad answer or after `timeoutMs`.
 */

/** The frame page: hashed in builds (`vite.sandbox.ts`), unhashed on the dev server. */
export const MERMAID_FRAME_PATH: string =
  (import.meta.env?.ESI_MERMAID_FRAME as string | undefined) ?? "/sandbox/mermaid.html";

/** The render budget, counted from the frame's load (design §4). */
export const MERMAID_TIMEOUT_MS = 5_000;
/** How long the frame may take to load its script first (about 5 MB, cached afterwards). */
export const MERMAID_LOAD_TIMEOUT_MS = 30_000;

// A drawn diagram is kept per key for the session (a few entries): an artefact version (immutable)
// or a Markdown fence's source, so reopening it, switching back or re-rendering a streamed answer
// draws nothing again.
const drawn = new Map<string, string>();
const DRAWN_ENTRIES = 24;
/** And at most this much SVG text in all (a drawing may be up to 4 MB). */
const DRAWN_CHARS = 8_000_000;
let drawnChars = 0;

/** A drawing already made for `key` this session, if any. */
export function drawnMermaid(key: string): string | undefined {
  return drawn.get(key);
}

/** Frames drawing at once: a page of fences never opens many ~5 MB frames together. */
export const MERMAID_CONCURRENT_FRAMES = 2;
let drawing = 0;
const waiting: Array<() => void> = [];

/** Waits for a drawing slot; resolves to its release, or rejects once `signal` aborts. */
function slot(signal: AbortSignal | undefined): Promise<() => void> {
  let released = false;
  const release = () => {
    if (released) {
      return;
    }
    released = true;
    drawing--;
    waiting.shift()?.();
  };
  if (drawing < MERMAID_CONCURRENT_FRAMES) {
    drawing++;
    return Promise.resolve(release);
  }
  return new Promise((resolve, reject) => {
    const take = () => {
      signal?.removeEventListener("abort", onAbort);
      drawing++;
      resolve(release);
    };
    const onAbort = () => {
      const at = waiting.indexOf(take);
      if (at >= 0) {
        waiting.splice(at, 1);
      }
      reject(new DiagramError("failed"));
    };
    waiting.push(take);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * `renderMermaid`, remembered per `key` (an artefact version, or {@link sourceKey} of a fence);
 * failures are not remembered. At most {@link MERMAID_CONCURRENT_FRAMES} draw at once; the others
 * wait in order, and an aborted one leaves the queue.
 */
export async function drawMermaid(
  key: string,
  source: string,
  options: { signal?: AbortSignal; host?: HTMLElement; timeoutMs?: number } = {},
): Promise<string> {
  const known = drawn.get(key);
  if (known !== undefined) {
    return known;
  }
  const release = await slot(options.signal);
  let svg: string;
  try {
    svg = await renderMermaid(source, options);
  } finally {
    release();
  }
  if (!drawn.has(key)) {
    drawn.set(key, svg);
    drawnChars += svg.length;
  }
  while (drawn.size > 1 && (drawn.size > DRAWN_ENTRIES || drawnChars > DRAWN_CHARS)) {
    const oldest = drawn.keys().next().value as string;
    drawnChars -= drawn.get(oldest)?.length ?? 0;
    drawn.delete(oldest);
  }
  return svg;
}

/**
 * A cache key for a diagram drawn from its source alone (a Markdown fence): a 53-bit hash
 * (cyrb53) and the length. A collision would only show a diagram this viewer already drew.
 */
export function sourceKey(source: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < source.length; i++) {
    const c = source.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hash = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return `md:${hash.toString(36)}:${source.length}`;
}

export class DiagramError extends Error {
  constructor(readonly reason: "failed" | "timeout") {
    super(`Diagram ${reason}`);
    this.name = "DiagramError";
  }
}

export function renderMermaid(
  source: string,
  options: {
    timeoutMs?: number;
    loadTimeoutMs?: number;
    signal?: AbortSignal;
    host?: HTMLElement;
  } = {},
): Promise<string> {
  if (options.signal?.aborted) {
    return Promise.reject(new DiagramError("failed"));
  }
  const host = options.host ?? document.body;
  const nonce = newNonce();
  const frame = document.createElement("iframe");
  frame.setAttribute("sandbox", "allow-scripts");
  frame.setAttribute("referrerpolicy", "no-referrer");
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.title = "";
  // Laid out (text is measured) but never seen or reached.
  Object.assign(frame.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: "1200px",
    height: "800px",
    border: "0",
    opacity: "0",
    pointerEvents: "none",
  });
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const finish = (outcome: { svg: string } | DiagramError) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      options.signal?.removeEventListener("abort", onAbort);
      frame.remove();
      if (outcome instanceof DiagramError) {
        reject(outcome);
      } else {
        resolve(outcome.svg);
      }
    };
    const onMessage = (event: MessageEvent) => {
      const result = acceptResult(event, frame.contentWindow, nonce);
      if (result !== null) {
        finish(result === "error" ? new DiagramError("failed") : result);
      }
    };
    const onAbort = () => finish(new DiagramError("failed"));
    // Loading the frame's script has its own, longer bound; the render budget starts at load.
    let timer = window.setTimeout(
      () => finish(new DiagramError("timeout")),
      options.loadTimeoutMs ?? MERMAID_LOAD_TIMEOUT_MS,
    );
    window.addEventListener("message", onMessage);
    options.signal?.addEventListener("abort", onAbort);
    frame.addEventListener(
      "load",
      () => {
        // The frame's origin is opaque, so it cannot be named: "*" is the only target. The frame
        // was just created by this document with this src, and the source is the viewer's own.
        // Once: a frame that navigated away never receives the source again.
        window.clearTimeout(timer);
        timer = window.setTimeout(
          () => finish(new DiagramError("timeout")),
          options.timeoutMs ?? MERMAID_TIMEOUT_MS,
        );
        frame.contentWindow?.postMessage({ type: RENDER_REQUEST, nonce, source }, "*");
      },
      { once: true },
    );
    frame.src = MERMAID_FRAME_PATH;
    host.append(frame);
  });
}
